import React, { useState } from "react";
import axios from "axios";
import { X, CalendarDays, Users, Clock, User, Mail, Phone, MessageSquare, Loader2 } from "lucide-react";
import { restaurantInfo } from "../data/mock";
import { useToast } from "../hooks/use-toast";
import { useLanguage } from "../context/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ReservationDialog({ open, onClose }) {
  const { toast } = useToast();
  const { t } = useLanguage();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", date: "", time: "19:00", guests: 2, notes: "",
  });

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/reservations`, {
        name: form.name,
        phone: form.phone,
        email: form.email,
        guests: Number(form.guests),
        date: form.date,
        time: form.time,
        notes: form.notes,
      }, { timeout: 20000 });

      // Also keep a local copy for the user's reference
      try {
        const arr = JSON.parse(localStorage.getItem("puri_reservations") || "[]");
        arr.push({ ...form, createdAt: new Date().toISOString() });
        localStorage.setItem("puri_reservations", JSON.stringify(arr));
      } catch {}

      toast({
        title: t.reserve.toastTitle,
        description: `${t.reserve.toastDescA}${form.name}${t.reserve.toastDescB}${form.date}${t.reserve.toastDescC}${form.time}${t.reserve.toastDescD}`,
      });
      setForm({ name: "", email: "", phone: "", date: "", time: "19:00", guests: 2, notes: "" });
      onClose();
    } catch (err) {
      console.error("Reservation error:", err);
      toast({
        title: t.reserve.errorTitle || "Fehler beim Senden",
        description: t.reserve.errorDesc || `Bitte versuchen Sie es erneut oder rufen Sie uns direkt an: ${restaurantInfo.phone}`,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm fade-in" onClick={onClose}>
      <div
        className="bg-[#FFF7EA] rounded-2xl shadow-2xl max-w-lg w-full relative overflow-hidden zoom-in border-2 border-[#D4A94A]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-r from-[#7A1F1F] to-[#5A1414] p-6 relative">
          <button onClick={onClose} className="absolute top-4 right-4 text-[#FFF7EA]/80 hover:text-white">
            <X size={22} />
          </button>
          <p className="section-sub text-[#E9C97A]">{t.reserve.sub}</p>
          <h3 className="font-display text-2xl md:text-3xl text-[#FFF7EA] mt-1 font-bold">{t.reserve.title}</h3>
          <p className="text-[#FBEFD9]/80 text-sm mt-1">{t.reserve.or}<a href={`tel:${restaurantInfo.phoneClean}`} className="underline">{restaurantInfo.phone}</a></p>
        </div>

        <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field icon={User} label={t.reserve.name} required value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <Field icon={Phone} label={t.reserve.phone} required type="tel" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
          <Field icon={Mail} label={t.reserve.email} type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
          <Field icon={Users} label={t.reserve.guests} type="number" min={1} max={20} required value={form.guests} onChange={(v) => setForm({ ...form, guests: v })} />
          <Field icon={CalendarDays} label={t.reserve.date} type="date" min={today} required value={form.date} onChange={(v) => setForm({ ...form, date: v })} />
          <Field icon={Clock} label={t.reserve.time} type="time" required value={form.time} onChange={(v) => setForm({ ...form, time: v })} />
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold uppercase tracking-widest text-[#7A1F1F] mb-2">
              <MessageSquare size={14} className="inline mr-1" /> {t.reserve.note}
            </label>
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full px-4 py-3 rounded-lg border border-[#D4A94A]/50 bg-white focus:outline-none focus:ring-2 focus:ring-[#E86D1F] transition"
              placeholder={t.reserve.placeholder}
            />
          </div>
          <div className="md:col-span-2 flex flex-col md:flex-row gap-3 mt-2">
            <button type="submit" disabled={submitting} className="btn-primary w-full justify-center disabled:opacity-70 disabled:cursor-not-allowed">
              {submitting && <Loader2 size={16} className="animate-spin" />}
              {submitting ? (t.reserve.sending || "Wird gesendet...") : t.reserve.submit}
            </button>
            <a href={`tel:${restaurantInfo.phoneClean}`} className="btn-outline w-full justify-center">
              <Phone size={16} /> {t.reserve.callInstead}
            </a>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, required, type = "text", value, onChange, min, max }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-widest text-[#7A1F1F] mb-2">
        <Icon size={14} className="inline mr-1" /> {label}{required && <span className="text-[#E86D1F]">*</span>}
      </label>
      <input
        type={type}
        required={required}
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 rounded-lg border border-[#D4A94A]/50 bg-white focus:outline-none focus:ring-2 focus:ring-[#E86D1F] transition"
      />
    </div>
  );
}
